import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Pencil, Trash2, Printer, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const empty = {
  employee_code: "", name: "", father_husband_name: "", date_of_birth: "", gender: "Male",
  address: "", mobile: "", email: "", aadhar_no: "", pan_no: "", bank_account_no: "", ifsc_code: "",
  department: "", designation: "", branch_id: "", date_of_joining: "", employment_type: "Full-Time", status: "Active",
  basic: 0, hra: 0, other_allowances: 0,
};
const Badge = ({ s }) => <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase border ${s === "Active" ? "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" : "text-muted-foreground bg-secondary border-border"}`}>{s}</span>;

// Module-level on purpose: defining this inside EmployeeMaster remounts the
// input on every keystroke (new component type each render) and focus is lost.
const Field = ({ k, label, type = "text", opts, f, setF }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    {opts ? (
      <Select value={f[k]} onValueChange={(v) => setF(x => ({ ...x, [k]: v }))}>
        <SelectTrigger data-testid={`emp-${k}`} className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
        <SelectContent>{opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    ) : (
      <Input data-testid={`emp-${k}`} type={type} value={f[k] ?? ""} onChange={(e) => setF(x => ({ ...x, [k]: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
    )}
  </div>
);

export default function EmployeeMaster() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);

  const load = () => api.get("/employees").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => {
    load();
    api.get("/departments").then(r => setDepts(r.data)).catch(() => {});
    api.get("/designations").then(r => setDesigs(r.data)).catch(() => {});
    api.get("/establishment").then(r => setBranches(r.data.branches || [])).catch(() => {});
  }, []);
  useEffect(() => { document.body.classList.toggle("txn-form-fullscreen", open); return () => document.body.classList.remove("txn-form-fullscreen"); }, [open]);

  const openNew = () => { setF(empty); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setF({ ...empty, ...r }); setEditId(r.id); setOpen(true); };
  const closeForm = () => { setOpen(false); setEditId(null); setF(empty); };

  const save = async () => {
    try {
      const payload = { ...f, basic: Number(f.basic) || 0, hra: Number(f.hra) || 0, other_allowances: Number(f.other_allowances) || 0 };
      if (editId) await api.patch(`/employees/${editId}`, payload);
      else await api.post("/employees", payload);
      toast.success(editId ? "Employee updated" : "Employee created");
      closeForm(); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (r) => {
    if (!confirm(`Delete employee "${r.name}"?`)) return;
    try { await api.delete(`/employees/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const print = (r) => window.print();

  if (open) {
    return (
      <div className="p-8" onKeyDownCapture={handleSimpleFormNav}>
        <div className="border border-border bg-card rounded-sm p-6 min-h-[70vh]" data-testid="employee-form-page">
          <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
            <div className="flex items-center gap-3">
              <Button variant="outline" data-testid="emp-back-btn" onClick={closeForm} className="rounded-sm text-xs uppercase tracking-wider"><ArrowLeft className="w-3 h-3 mr-1"/>Back</Button>
              <h2 className="font-display uppercase text-lg">{editId ? "Edit" : "New"} Employee</h2>
            </div>
            <Button variant="ghost" onClick={closeForm} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground">Cancel</Button>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <Field k="employee_code" label="Employee Code *" f={f} setF={setF}/>
            <Field k="name" label="Name *" f={f} setF={setF}/>
            <Field k="father_husband_name" label="Father / Husband Name" f={f} setF={setF}/>
            <Field k="date_of_birth" label="Date of Birth" type="date" f={f} setF={setF}/>
            <Field k="gender" label="Gender" opts={["Male", "Female", "Other"]} f={f} setF={setF}/>
            <Field k="mobile" label="Mobile" f={f} setF={setF}/>
            <Field k="email" label="Email" f={f} setF={setF}/>
            <Field k="aadhar_no" label="Aadhar No" f={f} setF={setF}/>
            <Field k="pan_no" label="PAN No" f={f} setF={setF}/>
            <Field k="bank_account_no" label="Bank Account No" f={f} setF={setF}/>
            <Field k="ifsc_code" label="IFSC Code" f={f} setF={setF}/>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Department</Label>
              <Select value={f.department} onValueChange={(v) => setF(x => ({ ...x, department: v }))}>
                <SelectTrigger data-testid="emp-department" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>{depts.filter(d => d.status !== "Inactive").map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Designation</Label>
              <Select value={f.designation} onValueChange={(v) => setF(x => ({ ...x, designation: v }))}>
                <SelectTrigger data-testid="emp-designation" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>{desigs.filter(d => d.status !== "Inactive").map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Establishment</Label>
              <Select value={f.branch_id} onValueChange={(v) => setF(x => ({ ...x, branch_id: v }))}>
                <SelectTrigger data-testid="emp-branch" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field k="date_of_joining" label="Date of Joining" type="date" f={f} setF={setF}/>
            <Field k="employment_type" label="Employment Type" opts={["Full-Time", "Part-Time", "Contract", "Intern"]} f={f} setF={setF}/>
            <Field k="status" label="Status" opts={["Active", "Inactive"]} f={f} setF={setF}/>
            <Field k="basic" label="Basic Salary" type="number" f={f} setF={setF}/>
            <Field k="hra" label="HRA" type="number" f={f} setF={setF}/>
            <Field k="other_allowances" label="Other Allowances" type="number" f={f} setF={setF}/>
          </div>
          <div className="mt-3">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
            <Input data-testid="emp-address" value={f.address ?? ""} onChange={(e) => setF(x => ({ ...x, address: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
          </div>
          <div className="flex justify-end gap-2 mt-6 border-t border-border pt-4">
            <Button variant="ghost" onClick={closeForm} className="rounded-sm text-xs uppercase tracking-wider">Cancel</Button>
            <Button data-testid="save-employee-btn" onClick={save} disabled={!f.employee_code || !f.name} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider">{editId ? "Update" : "Save"} Employee</Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Users className="w-3 h-3"/> HRMS</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Employee Master</h1>
        </div>
        <Button data-testid="add-employee-btn" onClick={openNew} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs"><Plus className="w-4 h-4 mr-1"/> New Employee</Button>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr><th className="text-left p-3">Code</th><th className="text-left p-3">Name</th><th className="text-left p-3">Designation</th><th className="text-left p-3">Department</th><th className="text-left p-3">Mobile</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No employees yet</td></tr>}
            {pg.pageItems.map(r => (
              <tr key={r.id} data-testid={`emp-row-${r.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary">{r.employee_code}</td><td className="p-3">{r.name}</td>
                <td className="p-3 text-xs">{r.designation || "-"}</td><td className="p-3 text-xs">{r.department || "-"}</td>
                <td className="p-3 text-xs font-mono">{r.mobile || "-"}</td><td className="p-3"><Badge s={r.status}/></td>
                <td className="p-3 text-right space-x-1">
                  <Button size="sm" variant="ghost" data-testid={`emp-edit-${r.id}`} onClick={() => openEdit(r)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button size="sm" variant="ghost" data-testid={`emp-del-${r.id}`} onClick={() => del(r)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                  <Button size="sm" variant="ghost" data-testid={`emp-print-${r.id}`} onClick={() => print(r)} className="h-7 w-7 p-0"><Printer className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
